import { useCallback, useEffect, useMemo, useRef, useState } from "react";
//formik
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import {
  FormikDateTimeField,
  FormikSelectField,
  FormikTextField,
} from "src/components/FormikComponents/FormikWrappers";
import { SwitchField } from "src/components/FormikComponents/SwitchField";
//material UI
import { Button, CardContent, Grid, Typography, useTheme, Theme, alpha } from "@mui/material";
import { IFlightAircraft, IFlightType, IGroundEvent, PolicyResource } from "@vmosolutions/oc-interfaces";

// API calls
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { DisplayLastModification, ILastModifiedInfo } from "src/components/FormikComponents/DisplayLastModification";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IAirportOption, IStructuresState } from "src/redux/reducers/StructuresReducer";
import { formatIdentFromAirport, updateAirportOptions } from "src/utils/airportHelper";

import { getFormFieldDiff, milli2DaysHrsMin } from "src/utils/index";
import { returnEpochValuesAsNumbers } from "../BetaEventEditor";
import { FormHeader, FormikFormIsDirtyEffect, FormTabId, IFormHandle } from "../helpers";

interface IGroundEventTabProps {
  groundEvent: IGroundEvent; // Id of the flight to retrieve and edit if provided.
  setLoadedGroundEvent: (eventValues: IGroundEvent) => void;
  updateTabState: (dirtyStatus: boolean) => void; // update parent with dirty status.
  mosaicId: string;
  setAPICallStatus: (statusMessage: string) => void;
  renderDialogModal: (title: string, message: string) => void;
  lastModifiedInfo: ILastModifiedInfo;
  registerFormHandle?: (tabId: string, handle: IFormHandle | null) => void;
  saveAllDirtyTabs?: (excludeTabId?: string) => Promise<void>;
}

interface IEditGroundEvent extends Partial<IGroundEvent> {
  comment?: string;
  reInitializeForm?: boolean;
}

//set initial value when user click "new flightType"
const initialGroundEventValues: IEditGroundEvent = {
  _id: undefined,
  title: undefined,
  eventType: undefined,
  scheduledStart: undefined,
  scheduledEnd: undefined,
  actualStart: undefined,
  actualEnd: undefined,
  location: undefined,
  note: undefined,
  aircraft: undefined,
  createdBy: undefined,
  createdAt: undefined,
  modifiedBy: undefined,
  modifiedAt: undefined,
};

const GridContainerStyles = (theme: Theme): any => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignContent: "flex-start",
  position: "relative",
  padding: "10px",
  flexGrow: 1,
  "& .MuiGrid-item": { width: 363 },
  "& .dialog_spinner_progress": { margin: -10 },
  "& .field-group": {
    flexDirection: "row",
    width: 380,
  },
  "& .formik_field": {
    width: 363,
    "&.note, &.comment": { width: 736 },
  },
  "& p.required_fields_label, & .crewslot_fields_label": {
    margin: 0,
    fontWeight: 300,
    fontSize: 10,
    textTransform: "uppercase",
    color: theme.custom.forms.labels,
  },
  "& .required_fields, & .crewslot_fields": {
    backgroundColor: alpha(theme.custom.forms.labels, 0.2),
    margin: "2px 0px",
  },
  "& .flight_number": {},
  "& .day_offset, .block_duration": {
    width: "auto",
    "& .input_control": {
      "& label": { width: 80 },
      "& div.input-base": {
        maxWidth: 100,
      },
    },
  },
  "& .oc_datetime": {
    // used in flight
    alignItems: "flex-start",
  },
  "& .scheduled_out, & .scheduled_in": {
    // used in recurring flight
    width: "auto",
    alignItems: "flex-start",
    "& .input_control": {
      "& label": { width: 80 },
      "& .time-input": { width: 70 },
    },
  },
  "& .position_count": {
    width: "auto",
    "& label": { width: "auto" },
    "& .input_control": {
      "& div.input-base": {
        maxWidth: 50,
      },
    },
  },
});

type FullGroundEventForm = FormikProps<IEditGroundEvent>;

//////////////////////////////////
// FORM Ground Event Validation //
//////////////////////////////////
const validateGroundEvent = (values: IEditGroundEvent): any => {
  const { scheduledStart, scheduledEnd, actualStart, actualEnd, location, eventType, note, aircraft, title } = values;
  // nested values have to be added here as empty objects.
  const errors: any = {};

  // Only validate if the form is dirty (has changes)
  if (!location) {
    errors.location = "Requires location ICAO.";
  }
  if (!scheduledStart) {
    errors.scheduledStart = "Requires scheduled start date and time.";
  }
  if (!scheduledEnd) {
    errors.scheduledEnd = "Requires scheduled end date and time.";
  }
  if (!actualStart) {
    errors.scheduledStart = "Requires actual start date and time.";
  }
  if (!actualEnd) {
    errors.scheduledEnd = "Requires actual end date and time.";
  }
  if (!eventType) {
    errors.eventType = "Requires an assigned event type.";
  }
  if (!aircraft) {
    errors.aircraft = "Requires an assigned aircraft.";
  }

  // Check if scheduled end is after start.
  if (!errors.scheduledStart && !errors.scheduledEnd && !!scheduledStart && !!scheduledEnd) {
    if (new Date(scheduledEnd) <= new Date(scheduledStart)) {
      errors.scheduledStart = "Event must start before it ends.";
      errors.scheduledEnd = "Event must end after it starts.";
    }
  }

  // Check if actual end is after start.
  if (!errors.actualStart && !errors.actualEnd && !!actualStart && !!actualEnd) {
    if (new Date(actualEnd) <= new Date(actualStart)) {
      errors.actualStart = "Event must start before it ends.";
      errors.actualEnd = "Event must end after it starts.";
    }
  }

  // Validate small note size
  if (!!note && note.length > 250) {
    errors.note = "Small note cannot exceed 250 characters.";
  }

  if (!!title && title.length > 20) {
    errors.title = "Title cannot exceed 20 characters.";
  }

  return errors;
};

/**
 * Base Ground Event Form
 * @param props -
 */
export const GroundEventTab = (props: IGroundEventTabProps): JSX.Element => {
  const { groundEvent, setLoadedGroundEvent, updateTabState, mosaicId, setAPICallStatus, lastModifiedInfo, registerFormHandle, saveAllDirtyTabs } = props;
  const mosaicPane = useMemo(() => document.getElementById(`${mosaicId}-panel`), [mosaicId]);
  const formikRef = useRef<FormikProps<IEditGroundEvent>>(null);

  // Register form handle so parent can trigger this tab's submit for save-all
  useEffect(() => {
    registerFormHandle?.(FormTabId.GroundEvent, {
      submitForm: () => formikRef.current?.submitForm() ?? Promise.resolve(),
      isDirty: () => formikRef.current?.dirty ?? false,
    });
    return () => { registerFormHandle?.(FormTabId.GroundEvent, null); };
  }, [registerFormHandle]);

  const values: IEditGroundEvent = useMemo(() => ({ ...initialGroundEventValues, ...groundEvent }), [groundEvent]);
  const { reInitializeForm, ...initialValues } = values;

  const { location } = initialValues;

  const theme = useTheme();
  const dispatch = useDispatch();

  // Retrieve Field Permission access check.
  const hasPermissions = useSelector((state: IRootState) => state.authentication.hasPermissions);

  // Structure dependencies
  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { airportOptions, customerStructure } = structures;
  const { flightTypes } = customerStructure;
  const groundEventTypes = useMemo(() => {
    return flightTypes?.length
      ? flightTypes
          .filter((type: IFlightType) => !type.isFlight)
          .sort((a, b) => {
            return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
          })
      : [];
  }, [flightTypes]);
  const aircraftOptions = customerStructure?.aircraft;

  const [displayAsUTC, setDisplayAsUTC] = useState<boolean>(true);

  // Retrieve initial AirportOptions for origin and destination
  useEffect(() => {
    if (location) void updateAirportOptions(location, airportOptions, dispatch);
  }, [location, airportOptions, dispatch]);

  /** GROUND EVENT CHANGE SUBMISSION */
  const handleSubmit = useCallback(
    async (values: IEditGroundEvent, helpers: FormikHelpers<IEditGroundEvent>): Promise<void> => {
      const { setSubmitting, setStatus, resetForm } = helpers;
      setSubmitting(true);
      setAPICallStatus("Updating Ground Event");
      const mode = values._id ? "update" : "create";
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: `${mode === "update" ? "Updating" : "Creating New"} Ground Event...`,
        successMessage: `Ground Event Successfully ${mode === "update" ? "Updated" : "Created"}.`,
        errorMessage: `Error: Unable to ${mode === "update" ? "Update" : "Create"} Ground Event`,
      };
      // Run a shallow comparison on top level values and return only changed fields. (for updates only)
      const groundEventUpdateDiff = getFormFieldDiff(values, initialValues);

      // Ensure comments or modifiedAt are never passed up to update call for ground events.
      //! comments can still be modified but that's only done in the comments tab which has it's own update call.
      delete groundEventUpdateDiff.comments;
      delete groundEventUpdateDiff.modifiedAt;

      // Should I pop up a modal? Make sure you check the client Name
      //TODO: Should this APIS Modal only show durning a certain time range?
      const response =
        mode === "create"
          ? await AmplifyAPIWrapper.Post<any>("/groundEvents/", values, notifications)
          : await AmplifyAPIWrapper.Put<any>(`/groundEvents/${values._id}`, groundEventUpdateDiff, notifications);
      if (response?._id) {
        //TODO Remove this function once the ground events endpoint is fixed to return correct "number" type for epochs.
        const fixedTimes = returnEpochValuesAsNumbers(response);
        setLoadedGroundEvent(fixedTimes);
        resetForm({ values: fixedTimes });
      }
      setSubmitting(false);
      setAPICallStatus(null);

      // Trigger save on all other dirty tabs
      await saveAllDirtyTabs?.(FormTabId.GroundEvent)
        ?.catch((error) => console.error("Failed to save all dirty tabs:", error));
    },
    [setLoadedGroundEvent, dispatch, setAPICallStatus, initialValues]
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={reInitializeForm}
      onSubmit={handleSubmit}
      // validationSchema={GroundEventTypeSchema} // Look into replacing validation with Yup.
      validate={validateGroundEvent}
      innerRef={formikRef}
    >
      {(eventForm: FullGroundEventForm) => {
        const { values, dirty, isValid, isSubmitting, handleReset } = eventForm;

        const blockDuration =
          values?.scheduledStart && values?.scheduledEnd
            ? milli2DaysHrsMin(values.scheduledEnd?.valueOf() - values.scheduledStart?.valueOf())
            : null;
        const subtitle = blockDuration ? `Block: ${blockDuration}` : "";

        return (
          <Form style={{ width: "100%" }}>
            <FormikFormIsDirtyEffect updateParent={updateTabState} formDirtyState={dirty} />
            <CardContent id={`${mosaicId}-groundevent-editor`} className="form_card groundevent">
              <Grid container direction="column">
                <FormHeader title="Ground Event" subtitle={subtitle} />
                <DisplayLastModification
                  userId={values.modifiedBy ?? values.createdBy}
                  time={values.modifiedAt ?? values.createdAt}
                  outsideModification={lastModifiedInfo}
                />
                <Grid container sx={GridContainerStyles(theme)} className={"form_body"}>
                  <FormikTextField
                    className="field textfield"
                    label="Title"
                    name="title"
                    multiline={false}
                    minRows={1}
                    maxRows={1}
                    disabled={!hasPermissions(PolicyResource.GroundEvent, "title")}
                  />
                  <Typography className="required_fields_label">required fields</Typography>

                  <Grid container direction="row" className="required_fields">
                    <FormikSelectField
                      className="eventTypegroup"
                      name="eventType"
                      label="Event Type"
                      isMulti={false}
                      options={groundEventTypes}
                      getOptionLabel={(type: IFlightType) => `${type.title} - ${type.description}`}
                      getOptionValue={(type: IFlightType) => type._id}
                      placeholder="Select EventType"
                      isSearchable={true}
                      disabled={!hasPermissions(PolicyResource.GroundEvent, "eventType")}
                      menuPortalTarget={mosaicPane}
                      menuPosition="fixed"
                    />
                    <FormikSelectField
                      label="Location"
                      name="location"
                      format={(ident: string) => formatIdentFromAirport(ident, Object.values(airportOptions))}
                      parse={(airport: IAirportOption) => airport?.ident}
                      onInputChange={(ident: string) => {
                        updateAirportOptions(ident, airportOptions, dispatch);
                      }}
                      options={Object.values(airportOptions)}
                      getOptionLabel={(airport: IAirportOption) =>
                        `${airport.ident}${airport?.iata ? `/${airport.iata}` : ""} - ${airport.name}`
                      }
                      getOptionValue={(airport: IAirportOption) => airport.ident}
                      isSearchable={true}
                      menuPortalTarget={mosaicPane}
                      menuPosition={"fixed"}
                      openMenuOnFocus={false}
                      isClearable={false}
                      noOptionsMessage={() => "Search airport name or ICAO..."}
                      disabled={!hasPermissions(PolicyResource.GroundEvent, "location")}
                    />

                    <Grid item>
                      {/* DISPLAY DATE/TIME LOCAL UTC SWITCH */}
                      <SwitchField
                        className={`time_mode_selector ${!displayAsUTC && "active"}`}
                        name={"editflight_time_mode_selector"}
                        value={displayAsUTC}
                        onChange={() => setDisplayAsUTC(!displayAsUTC)}
                        switchlabels={["UTC", "LOCAL"]}
                        switchtooltips={["Times as UTC", "Times as local"]}
                        label="Display Date/Times in"
                        allowNull={false}
                      />
                    </Grid>

                    <FormikDateTimeField
                      className="field datetimefield blocktimes"
                      label="Sched Start"
                      name="scheduledStart"
                      showAirportTime={values?.location}
                      localizeDisplay={!displayAsUTC}
                      hideLocalizeBtn={true}
                      isEpoch={true}
                      disabled={!hasPermissions(PolicyResource.GroundEvent, "scheduledStart")}
                    />

                    <FormikDateTimeField
                      className="field datetimefield blocktimes"
                      label="Sched End"
                      name="scheduledEnd"
                      showAirportTime={values?.location}
                      localizeDisplay={!displayAsUTC}
                      hideLocalizeBtn={true}
                      isEpoch={true}
                      disabled={!hasPermissions(PolicyResource.GroundEvent, "scheduledEnd")}
                    />

                    <FormikDateTimeField
                      className="field datetimefield blocktimes"
                      label="Actual Start"
                      name="actualStart"
                      showAirportTime={values?.location}
                      localizeDisplay={!displayAsUTC}
                      hideLocalizeBtn={true}
                      isEpoch={true}
                      disabled={!hasPermissions(PolicyResource.GroundEvent, "actualStart")}
                    />

                    <FormikDateTimeField
                      className="field datetimefield blocktimes"
                      label="Actual End"
                      name="actualEnd"
                      showAirportTime={values?.location}
                      localizeDisplay={!displayAsUTC}
                      hideLocalizeBtn={true}
                      isEpoch={true}
                      disabled={!hasPermissions(PolicyResource.GroundEvent, "actualEnd")}
                    />

                    <FormikSelectField
                      className="assignedAircraft"
                      name="aircraft"
                      label="Aircraft"
                      isMulti={false}
                      options={aircraftOptions}
                      // onChange={(ac) => assignDefaultConfiguration(ac, eventForm)}
                      getOptionLabel={(aircraft: IFlightAircraft) =>
                        `${aircraft.registration} (${aircraft.aircraftType && aircraft.aircraftType.title})`
                      }
                      getOptionValue={(option: any) => option._id}
                      // placeholder="Aircraft"
                      menuPortalTarget={mosaicPane}
                      menuPosition={"fixed"}
                      isSearchable={true}
                      isClearable={true}
                      disabled={!hasPermissions(PolicyResource.GroundEvent, "aircraft")}
                    />
                  </Grid>

                  <Grid container direction="row" className="optional_fields">
                    {/* <FormikTextField
                      className="field textfield multiline_long"
                      label="Title"
                      name="title"
                      multiline={false}
                      minRows={1}
                      maxRows={1}
                      disabled={!hasPermissions(PolicyResource.GroundEvent, "title")}
                    /> */}

                    <FormikTextField
                      className="field textfield multiline_long"
                      label="Small Note"
                      name="note"
                      multiline={true}
                      minRows={3}
                      maxRows={5}
                      disabled={!hasPermissions(PolicyResource.GroundEvent, "note")}
                    />
                  </Grid>

                  <Grid container className="form_footer">
                    <Button
                      className="btn reset"
                      type="reset"
                      variant="contained"
                      color="secondary"
                      disabled={(!dirty && isValid) || isSubmitting}
                      onClick={handleReset}
                    >
                      Reset
                    </Button>
                    <Button
                      className={"btn save"}
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting || !isValid || !dirty}
                      type="submit"
                    >
                      {values?._id ? "Update" : "Create"}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Form>
        );
      }}
    </Formik>
  );
};
